import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * A confirmação discreta do protótipo: aparece embaixo, some sozinha.
 *
 * Só para SUCESSO e aviso. Erro não vira toast — erro precisa dizer o
 * que fazer e ficar na tela até alguém resolver; um aviso que
 * desaparece em três segundos é a forma mais confiável de esconder um
 * problema de quem precisava vê-lo.
 */

type TipoDeToast = 'sucesso' | 'aviso';

const Contexto = createContext<((mensagem: string, tipo?: TipoDeToast) => void) | undefined>(
  undefined,
);

export function useToast() {
  const avisar = useContext(Contexto);
  if (!avisar) throw new Error('useToast precisa de <ProvedorDeToast> acima na árvore.');
  return avisar;
}

const DURACAO_MS = 3200;

export function ProvedorDeToast({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<{ mensagem: string; tipo: TipoDeToast } | undefined>();
  const temporizador = useRef<ReturnType<typeof setTimeout>>();

  const avisar = useCallback((mensagem: string, tipo: TipoDeToast = 'sucesso') => {
    setToast({ mensagem, tipo });
    clearTimeout(temporizador.current);
    temporizador.current = setTimeout(() => setToast(undefined), DURACAO_MS);
  }, []);

  const valor = useMemo(() => avisar, [avisar]);

  return (
    <Contexto.Provider value={valor}>
      {children}
      {/*
        `aria-live="polite"` e não `assertive`: a confirmação é
        informação secundária e não deve interromper quem está no meio
        de outra coisa com um leitor de tela.
      */}
      <div aria-live="polite" aria-atomic="true">
        {toast && (
          <div
            className="toast on"
            style={toast.tipo === 'aviso' ? { borderLeftColor: 'var(--amber)' } : undefined}
          >
            {toast.mensagem}
          </div>
        )}
      </div>
    </Contexto.Provider>
  );
}
